import { BundleItemDataExtended } from '@/api';
import { BundleTreeTypes, SectionsTreeNode } from '@repo/atomic-components';
import { GridDataGroupIds, GridGroup, GridItem, GroupSectionLabel } from '../types';
import { PageInfoData } from '@/store';

/**
 * DEEPEST_LABEL_LEVEL - is the deepest level of labels we want to assign.
 * We can use this constant to determine the level of the label based on the depth of the node in the tree
 * For example, if the root node is at level 0, its children will be at level 1, but level 1 is deepest level
 * so we set it as 4 to understand that it is the deepest level of labels.
 * This is useful for the case when we have a tree with multiple levels and we want to assign labels based on the depth of the node.
 */
const DEEPEST_LABEL_LEVEL = 4;
const LEVEL_INCREMENT = 1;
const UNNAMED_GROUP_LABEL = 'Unnamed Group';

export const extractGroupSectionLabelsAndPageData = (
  sections: SectionsTreeNode[],
  pageIndexMap: Map<string, number>,
  pageDataMap: Map<string, BundleItemDataExtended>,
  pageInfoCache?: Record<string, PageInfoData>,
): GridGroup[] => {
  const result: GridGroup[] = [];

  const traverse = (
    nodes: SectionsTreeNode[],
    currentLabels: GroupSectionLabel[],
    currentGroupIds: GridDataGroupIds,
  ) => {
    nodes.forEach((node) => {
      const newLabel: GroupSectionLabel = {
        id: node.id,
        type: node.type,
        text: node.name || UNNAMED_GROUP_LABEL,
        level: node.children?.length ? currentLabels.length + LEVEL_INCREMENT : DEEPEST_LABEL_LEVEL,
      };
      const newLabels = [...currentLabels, newLabel];

      const newGroupIds: GridDataGroupIds = {
        ...currentGroupIds,
        ...(node.type === BundleTreeTypes.PROVIDER && {
          groupProviderId: node.id,
        }),
        ...(node.type === BundleTreeTypes.CATEGORY && {
          groupCategoryId: node.id,
        }),
        ...(node.type === BundleTreeTypes.SUB_CATEGORY && {
          groupSubCategoryId: node.id,
        }),
        ...(node.type === BundleTreeTypes.CLUSTER && {
          groupClusterId: node.id,
        }),
        ...(node.type === BundleTreeTypes.DOCUMENT && {
          groupDocumentId: node.id,
        }),
      };

      // if node has pageIds, create GridGroup
      if (node.pageIds?.length) {
        // ID GridGroup is ID the deepest label in the group
        const gridGroupId = newLabels[newLabels.length - 1].id;

        // Create a GridItem array, ensuring that pageData and pageIndex are always valid
        const validGridItems: GridItem[] = [];

        node.pageIds.forEach((pageId) => {
          const pageIndex = pageIndexMap.get(pageId);
          const pageData = pageDataMap.get(pageId);

          // Check the availability and validity of data before creating a GridItem
          if (typeof pageIndex !== 'number' || pageIndex < 0) {
            console.log(`Can not find index for page ${pageId}. Index: ${pageIndex}`);
            return; // Skip this pageId
          }
          if (!pageData) {
            console.log(`Can not find data for page ${pageId}.`);
            return; // Skip this pageId
          }

          // Merge cached page info if available
          const cachedInfo = pageInfoCache?.[pageId];
          const mergedPageData: BundleItemDataExtended = cachedInfo
            ? {
                ...pageData,
                highRelevance: cachedInfo.highRelevance ?? pageData.highRelevance,
                inLightBundle: cachedInfo.inLightBundle ?? pageData.inLightBundle,
                chronologyEntryIds: cachedInfo.chronologyEntryIds ?? pageData.chronologyEntryIds,
              }
            : pageData;

          // If all data is valid, add the GridItem to the array
          validGridItems.push({
            id: pageId,
            pageIndex: pageIndex,
            pageData: mergedPageData,
          });
        });

        result.push({
          id: gridGroupId,
          labels: newLabels,
          items: validGridItems,
          ...newGroupIds,
        });
      }

      // Recursively process child elements
      if (node.children?.length) {
        traverse(node.children, newLabels, newGroupIds);
      }
    });
  };

  traverse(sections, [], {});
  return result;
};
